<template>
  <div :class="[props.type, props.className, { 'auto-height': props.autoHeight }]" id="dialog">
    <el-dialog
      :class="['my-dialog', props.className, { 'auto-height': props.autoHeight }]"
      v-model="dialogVisible"
      :title="props.title"
      :width="props.width"
      :draggable="draggable"
      :destroy-on-close="true"
      :append-to-body="props.appendToBody"
      :top="isFullscreen ? '0' : '10vh'"
      :before-close="handleBeforeClose"
      @open="handleOpen"
      @close="handleClose"
    >
      <div v-if="props.strategyBox" class="full-screen strategyBoxIcon" @click="handlePreview">
        <el-icon>
          <Reading />
        </el-icon>
      </div>
      <div v-if="!props.hideFullScreen" class="dialog-fullscreen" @click="fullScreen">
        <el-icon>
          <FullScreen />
        </el-icon>
      </div>

      <div class="my-dialog-content scrollbar">
        <slot />
      </div>

      <template #footer v-if="!hideFooter">
        <div class="my-dialog-footer">
          <slot name="footer" />
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { nextTick, ref, watch, provide, readonly } from 'vue'
import { FullScreen, Reading } from '@element-plus/icons-vue'

const props = defineProps({
  title: String,
  visible: {
    type: Boolean,
    default: false,
  },
  width: String,
  beforeClose: {
    type: Function,
  },
  hideFooter: {
    type: Boolean,
    default: false,
  },
  hideFullScreen: {
    type: Boolean,
    default: false,
  },
  strategyBox: {
    type: Boolean,
    default: false,
  },
  draggable: {
    type: Boolean,
    default: false,
  },
  type: {
    type: String,
  },
  autoHeight: {
    type: Boolean,
    default: false,
  },
  appendToBody: {
    type: Boolean,
    default: false,
  },
  className: String,

  // 弹窗全屏
  isExternalFullscreen: {
    type: Boolean,
    default: false,
  },
})

const dialogVisible = ref(false)
watch(
  () => props.visible,
  () => {
    dialogVisible.value = props.visible
  },
  { immediate: true },
)

// 监听 isExternalFullscreen 标识，如果为true，证明是从移动端进入的，则全屏显示。
watch(
  () => props.isExternalFullscreen,
  val => {
    if (val) {
      nextTick(() => {
        fullScreen()
      })
    }
  },
  { immediate: true },
)

const emits = defineEmits(['open', 'close', 'update:visible', 'fullscreen', 'openStrategyBox'])
// 打开弹窗
const handleOpen = () => {
  emits('open')
  emits('update:visible', true)
}
// 关闭弹窗
const handleClose = () => {
  // cancelFullscreen()
  emits('close')
  emits('update:visible', false)
}

// 打开策略参数浮窗
function handlePreview() {
  emits('openStrategyBox')
}

// 关闭前回调
const handleBeforeClose = done => {
  if (!props.beforeClose) {
    done()
  } else if (typeof props.beforeClose === 'function') {
    props.beforeClose(done)
  } else {
    done()
  }
}

let isFullscreen = ref(false)

/**
 * 全屏
 */
const fullScreen = () => {
  const dialog = document.querySelectorAll(`${props.className ? '.' + props.className : '#dialog'} .el-dialog`)
  if (dialog[0]?.classList.contains('is-fullscreen')) {
    dialog[0]?.classList.remove('is-fullscreen')
  } else {
    dialog[0]?.classList.add('is-fullscreen')
  }
  nextTick(() => {
    isFullscreen.value = !isFullscreen.value
    emits('fullscreen')
  })
}
//取消全屏
const cancelFullscreen = () => {
  const dialog = document.querySelectorAll(`${props.className ? '.' + props.className : '#dialog'} .el-dialog`)
  dialog[0].classList.remove('is-fullscreen')
  emits('fullscreen')
  nextTick(() => {
    isFullscreen.value = false
  })
}
// provide('isFullscreen', isFullscreen)
</script>

<style lang="scss">
.is-fullscreen {
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100% !important;
  height: 100% !important;
  margin: 0;
  transform: none !important;
  .my-dialog-content {
    max-height: 100%;
    box-sizing: border-box;
  }
}
.my-dialog {
  height: 80%;
  min-height: 500px;
  display: flex;
  flex-direction: column;

  .dialog-fullscreen,
  .strategyBoxIcon {
    position: absolute;
    top: 12px;
    right: 44px;
    z-index: 100;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: var(--ui-dialog-title-color, #fff);
    font-size: 16px;
    line-height: 1;

    .el-icon {
      font-size: 16px;
      color: inherit;
    }

    &:hover {
      color: var(--el-color-primary);
    }
  }
  .strategyBoxIcon {
    right: 76px;
  }

  .el-dialog__header {
    border-bottom: none;
    margin: 0;
    padding: 15px 20px;
  }
  .el-dialog__close {
    color: var(--ui-dialog-title-color, #fff);
    &:hover {
      color: var(--el-color-primary);
    }
  }
  .el-dialog__footer {
    padding: 0;
  }
  .el-dialog__body {
    flex: 1;
    padding: 0;
    overflow: hidden;
  }
  .my-dialog-content {
    overflow: auto;
    padding: 20px;
    height: 100%;
    box-sizing: border-box;
  }
  .my-dialog-footer {
    padding: 10px;
    border-top: none;
    text-align: right;
    box-sizing: border-box;
  }
}

/* append-to-body 时 el-dialog 会挂到 body，必须用全局类才能命中 */
.my-dialog.auto-height {
  height: auto !important;
  min-height: auto !important;

  .el-dialog__body {
    flex: none;
  }

  .my-dialog-content {
    height: auto !important;
  }
}
</style>

<style scoped lang="scss">
.auto-height {
  :deep(.el-dialog) {
    height: auto;
    min-height: auto;

    .el-dialog__body {
      flex: none;
    }

    .my-dialog-content {
      height: auto;
    }
  }
}

.dark {
  :deep(.el-dialog) {
    background: var(--ui-dialog-bg, #061e48);
    background-size: 100% 100%;
    padding: 12px 16px 8px;
    position: relative;

    .el-dialog__header {
      position: relative;
      top: auto;
      left: auto;
      right: auto;
      z-index: 10;
      padding: 8px 40px 12px 8px;
      border-bottom: none !important;
      background: var(--ui-dialog-header-bg, transparent);

      .el-dialog__title {
        color: var(--ui-dialog-title-color, #fff);
        font-size: 18px;
        font-weight: 600;
      }

      .el-dialog__headerbtn {
        top: 4px;
        right: 4px;
        width: 32px;
        height: 32px;
        .el-dialog__close {
          color: var(--ui-dialog-title-color, #fff);
        }
      }
    }
    .my-dialog-content {
      padding-top: 8px;
    }
    .my-dialog-footer {
      border: none;
    }
  }
}
</style>
